import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getSeoBaseUrl } from '../../utils/api';
import AuthBounceSpinner from './AuthBounceSpinner';

export default function ProtectedRoute({ children, permission, requireKb, requirePlan, requireRole }) {
    const { isAuthenticated, isLoading, user } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return <AuthBounceSpinner />;
    }

    // Sign-in lives in the Next.js seo app at a different origin in dev (port 3009);
    // resolve the full URL via getSeoBaseUrl(). A full-page navigation is required —
    // react-router's <Navigate> would resolve relative to /app/.
    if (!isAuthenticated) {
        if (typeof window !== 'undefined') {
            window.location.replace(`${getSeoBaseUrl()}/sign-in`);
        }
        return <AuthBounceSpinner />;
    }

    // Super admins are back-office only — they shouldn't end up inside the
    // merchant-facing app routes (dashboard, designer, my-pages, …) by typing
    // a URL or following an old bookmark. Bounce them back to /admin where
    // their tools live. The seed gives the role merchant-side permissions for
    // future flexibility, so we can't gate purely on the permission list.
    if (user?.role === 'superadmin' && !location.pathname.startsWith('/admin')) {
        return <Navigate to="/admin" replace />;
    }

    // Redirect to onboarding if not completed (preserve query params like ?shop=)
    if (user?.onboardingCompleted === false && location.pathname !== '/onboarding') {
        // Redirect to onboarding if not completed (preserve query params like ?shop=).
        // /brand-onboarding is the dedicated brand-user wizard reached from the SEO
        // /get-started entry point — do NOT bounce those users back into the
        // standard /onboarding flow.
        const onboardingPaths = ['/onboarding', '/brand-onboarding'];
        const onAnOnboardingPath = onboardingPaths.some(
            (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
        );
        if (user?.onboardingCompleted === false && !onAnOnboardingPath) {
            const search = location.search || '';
            return <Navigate to={`/onboarding${search}`} replace />;
        }

        // Check route-level permission
        if (permission) {
            const permissions = user?.permissions || [];
            if (!permissions.includes(permission)) {
                return <Navigate to="/dashboard" replace />;
            }
        }

        // Block access if Knowledge Base is not connected
        if (requireKb && !user?.kbConnected) {
            return <Navigate to="/dashboard" replace />;
        }

        // Role-based guard. `requireRole` may be a string or array of allowed roles.
        if (requireRole) {
            const allowed = Array.isArray(requireRole) ? requireRole : [requireRole];
            const role = String(user?.role || '').trim().toLowerCase();
            if (!allowed.map((r) => String(r).toLowerCase()).includes(role)) {
                return <Navigate to="/dashboard" replace />;
            }
        }

        // Plan-based route guard
        if (requirePlan) {
            const userPlan = user?.subscription?.planKey || null;
            const tierOrder = { STARTER: 1, PRO: 2 };
            if ((tierOrder[userPlan] || 0) < (tierOrder[requirePlan] || 0)) {
                return <Navigate to="/settings" state={{ showUpgrade: true, requiredPlan: requirePlan }} replace />;
            }
        }

    }

    return children;
}
